import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Header, Icon, Button, Grid, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import UploadButton from "../../../../DataImport/UploadButton/UploadButton";
import { validateMassSimulationCsv, getMassSimulationTemplate } from "../../../../../actions/analytics-v2";
import "./UploadSimulationInputsModal.css";

export const UploadSimulationInputsModal = ({
    currentWorkspaceId,
    stagedFile,
    stagedAt,
    onStaged,
    onCleared,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const fileInputRef = useRef();

    const [selectedFile, setSelectedFile] = useState(stagedFile);
    const [staged, setStaged] = useState(!!stagedFile);
    const [validating, setValidating] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [fetchingTemplate, setFetchingTemplate] = useState(false);

    const openFileDialog = () => {
        fileInputRef.current.click();
    };

    const stageFile = async (file) => {
        if (!file || validating) return;
        setValidating(true);
        setValidationErrors([]);
        const result = await validateMassSimulationCsv(currentWorkspaceId, file);
        if (result.status === "ok") {
            const stagedTimestamp = Date.now();
            setStaged(true);
            onStaged(file, stagedTimestamp);
        } else {
            setStaged(false);
            if (result.errors) {
                setValidationErrors(result.errors);
            }
        }
        setValidating(false);
    };

    const onFileAdded = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        setStaged(false);
        setValidationErrors([]);
        onCleared();
        stageFile(file);
    };

    const handleDownloadTemplate = async () => {
        if (fetchingTemplate) return;
        setFetchingTemplate(true);
        await getMassSimulationTemplate();
        setFetchingTemplate(false);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setStaged(false);
        setValidationErrors([]);
        onCleared();
    };

    return (
        <Modal open size="small" onClose={onClose}>
            <Header icon="file alternate outline" content={t("account.adminConsole.massSimulationUploadInputs")} />
            <Modal.Content>
                <p style={{ opacity: 0.75 }}>
                    {t("account.adminConsole.massSimulationUploadInputsDisclaimer")}
                </p>
                <Grid columns={2} stackable>
                    <Grid.Column style={{ width: "auto" }}>
                        <UploadButton
                            disabled={validating}
                            align="left"
                            text={t("account.adminConsole.massSimulationUploadCsv")}
                            fileInputRef={fileInputRef}
                            openFileDialog={openFileDialog}
                            onFileAdded={onFileAdded}
                        />
                    </Grid.Column>
                    <Grid.Column style={{ flex: 1, display: "flex", alignItems: "center" }}>
                        {
                            selectedFile
                                ? <span>{selectedFile.name}</span>
                                : <span style={{ opacity: 0.6 }}>{t("account.adminConsole.massSimulationNoFileSelected")}</span>
                        }
                    </Grid.Column>
                </Grid>

                <div className="mass-simulation-staged-status">
                    <Icon
                        name={
                            validating
                                ? "circle notched"
                                : staged
                                    ? "check circle"
                                    : validationErrors.length > 0
                                        ? "warning circle"
                                        : "circle outline"
                        }
                        loading={validating}
                        color={
                            validating
                                ? "grey"
                                : staged
                                    ? "green"
                                    : validationErrors.length > 0
                                        ? "red"
                                        : "grey"
                        }
                    />
                    {
                        validating
                            ?   <span>{t("account.adminConsole.massSimulationValidatingFile")}</span>
                            :   staged
                                    ?   <span>
                                            {t("account.adminConsole.massSimulationFileStagedLabel")}: {selectedFile.name}
                                            {" "}({t("account.adminConsole.massSimulationStagedAtLabel")} {new Date(stagedAt).toLocaleString(i18n.language)})
                                        </span>
                                    :   <span>{t("account.adminConsole.massSimulationNoFileStaged")}</span>
                    }
                    {
                        staged &&
                        <Icon
                            name="close"
                            link
                            className="mass-simulation-remove-staged-file"
                            onClick={handleRemoveFile}
                        />
                    }
                </div>

                {
                    validationErrors.length > 0 &&
                    <div className="mass-simulation-csv-errors">
                        <label className="field-title">
                            {t("account.adminConsole.massSimulationValidationErrorsTitle")}
                        </label>
                        <Table celled compact size="small">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorMaterialColumn")}</Table.HeaderCell>
                                    <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorColumnColumn")}</Table.HeaderCell>
                                    <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorMessageColumn")}</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {
                                    validationErrors.map((error, index) => (
                                        <Table.Row key={`${error.material_id}-${error.column}-${index}`}>
                                            <Table.Cell>{error.material_id || "-"}</Table.Cell>
                                            <Table.Cell>{error.column || "-"}</Table.Cell>
                                            <Table.Cell>{(error.message || []).join(" ")}</Table.Cell>
                                        </Table.Row>
                                    ))
                                }
                            </Table.Body>
                        </Table>
                    </div>
                }
            </Modal.Content>
            <Modal.Actions className="modal-actions-custom">
                <Button
                    className="download-btn"
                    onClick={handleDownloadTemplate}
                    loading={fetchingTemplate}
                    disabled={fetchingTemplate}
                    type="button"
                >
                    {t("account.adminConsole.massSimulationDownloadTemplate")}
                </Button>

                <div className="right-buttons">
                    <Button type="button" color="red" inverted onClick={onClose}>
                        <Icon name="close" /> {staged ? t("close") : t("cancel")}
                    </Button>
                </div>
            </Modal.Actions>
        </Modal>
    );
};

UploadSimulationInputsModal.propTypes = {
    currentWorkspaceId: PropTypes.number,
    stagedFile: PropTypes.object,
    stagedAt: PropTypes.number,
    onStaged: PropTypes.func.isRequired,
    onCleared: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default UploadSimulationInputsModal;
